import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useAuth } from '../../context/AuthContext';
import { getEdgeFunctionHeaders } from '../../lib/edgeFunctions';
import { getSupabaseFunctionUrl } from '../../lib/supabase';

const plans = [
  {
    name: 'Free',
    price: { monthly: '$0', annual: '$0' },
    credits: 25,
    creditColor: 'text-stone-600',
    description: 'Try AdForge and see what AI ad copy can do for your business.',
    primaryOutput: '2 standard ads / month',
    secondaryOutput: 'or 8 social posts',
    tertiaryOutput: '3 social posts with images',
    creditAllowance: '25 credits included',
    highlight: false,
    badge: null,
    features: {
      'Ad channels': 'Facebook & Instagram ad formats',
      'Social content': 'Social posts for 1 platform',
      'Ad copy package': 'Hooks, headlines, body copy, and CTAs included',
      'Creative tools': '1 image per generation + 2 free edits per ad',
      'Save ads library': false,
      'History & exports': false,
      'Team members': false,
      'API access': false,
      'Priority support': false,
      'White-label exports': false,
    },
    cta: 'Start Free',
    ctaLink: '/signup',
    ctaStyle:
      'border border-stone-200 bg-white text-stone-800 hover:border-orange-400 hover:text-orange-500',
  },
  {
    name: 'Pro',
    price: { monthly: '$29', annual: '$23' },
    credits: 250,
    creditColor: 'text-orange-100',
    description:
      'For serious business owners who run ads regularly and want consistent results.',
    primaryOutput: '25 standard ads / month',
    secondaryOutput: 'or 83 social posts',
    tertiaryOutput: '31 social posts with images or 2 short videos',
    creditAllowance: '250 credits included',
    highlight: true,
    badge: 'Most Popular',
    features: {
      'Ad channels': 'All 5 ad platforms',
      'Social content': 'Social posts for all 6 platforms',
      'Ad copy package': 'Expanded hooks, headlines, body copy, and CTAs',
      'Creative tools': '3 images per generation + 2 free edits per ad',
      'Editor mode': true,
      'Video ads': true,
      'Save ads library': true,
      'History & exports': true,
      'Team members': '1 seat',
      'API access': false,
      'Priority support': true,
      'White-label exports': false,
    },
    cta: 'Start Pro Free',
    ctaLink: '/signup',
    ctaStyle:
      'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500',
  },
  {
    name: 'Agency',
    price: { monthly: '$99', annual: '$79' },
    credits: 1000,
    creditColor: 'text-amber-700',
    description:
      'For agencies and teams managing ads for multiple clients at scale.',
    primaryOutput: '100 standard ads / month',
    secondaryOutput: 'or 333 social posts',
    tertiaryOutput: '125 social posts with images or 10 premium videos',
    creditAllowance: '1,000 credits included',
    highlight: false,
    badge: 'Best for Agencies',
    features: {
      'Ad channels': 'All 5 ad platforms',
      'Social content': 'Social posts for all 6 platforms',
      'Ad copy package': 'Expanded hooks, headlines, body copy, and CTAs',
      'Creative tools': '6 images per generation + 2 free edits per ad',
      'Editor mode': true,
      'Video ads': true,
      'Save ads library': true,
      'History & exports': true,
      'Team members': '5 seats',
      'Priority support': true,
    },
    cta: 'Start Agency Free',
    ctaLink: '/signup',
    ctaStyle: 'bg-stone-900 text-white hover:bg-stone-800',
  },
];

const featureRows = [
  'Ad channels',
  'Social content',
  'Ad copy package',
  'Creative tools',
  'Video ads',
  'Save ads library',
  'History & exports',
  'Team members',
  'Priority support',
];

const CREDIT_COSTS_DISPLAY = [
  { action: 'Generate 1 ad (image + copy)', cost: '10 credits', icon: 'ri-flashlight-fill' },
  { action: '3-image standard bundle', cost: '27 credits', icon: 'ri-stack-line' },
  { action: '6-image standard bundle (Agency)', cost: '48 credits', icon: 'ri-stack-line' },
  { action: 'Generate social post (captions + hooks + CTAs)', cost: '3 credits', icon: 'ri-sparkling-2-fill', isNew: true },
  { action: 'Add AI image to social post', cost: '+5 credits (8 total)', icon: 'ri-image-line', isNew: true },
  { action: '5-sec video ad', cost: '50 credits', icon: 'ri-movie-line' },
  { action: '10-sec video ad', cost: '100 credits', icon: 'ri-movie-2-line' },
  { action: 'Voiceover / sound effect / music', cost: '10 / 10 / 20 credits', icon: 'ri-volume-up-line' },
  { action: 'Re-generate image only', cost: '5 credits', icon: 'ri-image-edit-line' },
  { action: 'Generate variation', cost: '5 credits', icon: 'ri-refresh-line' },
  { action: 'Rewrite ad copy', cost: '2 credits', icon: 'ri-edit-line' },
];

const pricingFaqs = [
  {
    q: 'Can I switch plans at any time?',
    a: 'Absolutely. Upgrade or downgrade whenever you need. Changes take effect at the start of your next billing cycle.',
  },
  {
    q: 'What counts as one "generation"?',
    a: 'One generation = one full set of hooks, headlines, body copy, and CTAs for a single ad campaign input.',
  },
  { q: 'Do unused generations roll over?', a: 'Subscription credits reset monthly, top-up credits roll over.' },
];

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = async (planName: string) => {
    if (!user) {
      navigate('/signup');
      return;
    }

    const planKey = planName.toLowerCase() as 'pro' | 'agency';
    setLoadingPlan(planKey);
    setCheckoutError(null);

    try {
      const response = await fetch(
        getSupabaseFunctionUrl('create-checkout-session'),
        {
          method: 'POST',
          headers: await getEdgeFunctionHeaders(),
          body: JSON.stringify({
            plan: planKey,
            billingInterval: annual ? 'annual' : 'monthly',
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.url) {
        throw new Error(data.error ?? 'Failed to create checkout session.');
      }

      window.location.href = data.url;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setCheckoutError(message);
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      <main className="pt-24">
        <section className="py-16 text-center px-6">
          <span className="inline-block text-sm font-semibold text-orange-500 uppercase tracking-widest mb-3">
            Pricing
          </span>
          <h1 className="text-5xl md:text-6xl font-extrabold text-stone-900 mb-4">
            Start free. Scale fast.
            <br />
            <span className="text-orange-500">Pay for what you need.</span>
          </h1>
          <p className="text-lg text-stone-500 max-w-xl mx-auto mb-8">
            No contracts. No hidden fees. Upgrade or cancel anytime.
          </p>

          <div className="inline-flex items-center bg-stone-100 rounded-full p-1 gap-1">
            <button
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                !annual ? 'bg-white text-stone-900' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                annual ? 'bg-white text-stone-900' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              Annual
              <span className="text-xs font-bold text-orange-500 bg-orange-100 px-2 py-0.5 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </section>

        {checkoutError && (
          <div className="max-w-md mx-auto mb-4 px-6">
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 text-center">
              {checkoutError}
            </div>
          </div>
        )}

        <section className="px-6 pb-16">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map((plan) => {
                const isPaid = plan.name === 'Pro' || plan.name === 'Agency';
                const isLoading = loadingPlan === plan.name.toLowerCase();

                return (
                  <div
                    key={plan.name}
                    className={`relative rounded-2xl border ${
                      plan.highlight
                        ? 'border-orange-300 bg-gradient-to-b from-orange-500 to-amber-500 text-white'
                        : 'border-stone-200 bg-white text-stone-900'
                    } p-8`}
                  >
                    {plan.badge && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                        <span
                          className={`text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap ${
                            plan.highlight ? 'bg-stone-900 text-white' : 'bg-orange-500 text-white'
                          }`}
                        >
                          {plan.badge}
                        </span>
                      </div>
                    )}

                    <div className="mb-7">
                      <div
                        className={`text-xs font-bold uppercase tracking-widest mb-1 ${
                          plan.highlight ? 'text-white/70' : 'text-orange-500'
                        }`}
                      >
                        {plan.name}
                      </div>
                      <div className="flex items-end gap-1 mb-2">
                        <span
                          className={`text-5xl font-black ${
                            plan.highlight ? 'text-white' : 'text-stone-900'
                          }`}
                        >
                          {annual ? plan.price.annual : plan.price.monthly}
                        </span>
                        <span
                          className={`text-sm mb-2 ${
                            plan.highlight ? 'text-white/70' : 'text-stone-400'
                          }`}
                        >
                          /month
                        </span>
                      </div>
                      {annual && plan.name !== 'Free' && (
                        <p
                          className={`text-xs mb-2 ${
                            plan.highlight ? 'text-white/70' : 'text-stone-400'
                          }`}
                        >
                          Billed annually
                        </p>
                      )}
                      <p
                        className={`text-sm leading-relaxed ${
                          plan.highlight ? 'text-white/80' : 'text-stone-500'
                        }`}
                      >
                        {plan.description}
                      </p>
                      <div
                        className={`mt-5 rounded-2xl border px-4 py-4 ${
                          plan.highlight
                            ? 'border-white/20 bg-white/15'
                            : 'border-orange-100 bg-orange-50/60'
                        }`}
                      >
                        <div
                          className={`text-[11px] font-bold uppercase tracking-widest ${
                            plan.highlight ? 'text-white/70' : 'text-orange-500'
                          }`}
                        >
                          Typical monthly output
                        </div>
                        <div
                          className={`mt-2 text-xl font-black leading-tight ${
                            plan.highlight ? 'text-white' : 'text-stone-900'
                          }`}
                        >
                          {plan.primaryOutput}
                        </div>
                        <div
                          className={`mt-1 text-sm font-semibold ${
                            plan.highlight ? 'text-white/85' : 'text-stone-700'
                          }`}
                        >
                          {plan.secondaryOutput}
                        </div>
                        <div
                          className={`mt-1 text-xs leading-relaxed ${
                            plan.highlight ? 'text-white/70' : 'text-stone-500'
                          }`}
                        >
                          {plan.tertiaryOutput}
                        </div>
                        <div
                          className={`mt-3 inline-flex rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${
                            plan.highlight
                              ? 'bg-white/20 text-white'
                              : 'bg-white text-stone-600 ring-1 ring-orange-100'
                          }`}
                        >
                          {plan.creditAllowance}
                        </div>
                      </div>
                    </div>

                    {isPaid ? (
                      <button
                        onClick={() => handleCheckout(plan.name)}
                        disabled={isLoading}
                        className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-bold transition-all duration-200 whitespace-nowrap mb-7 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed ${
                          plan.highlight ? 'bg-white text-orange-600 hover:bg-orange-50' : plan.ctaStyle
                        }`}
                      >
                        {isLoading ? (
                          <>
                            <i className="ri-loader-4-line animate-spin" />
                            Redirecting...
                          </>
                        ) : (
                          plan.cta
                        )}
                      </button>
                    ) : (
                      <Link
                        to={plan.ctaLink}
                        className={`block text-center py-3.5 rounded-xl text-sm font-bold transition-all duration-200 whitespace-nowrap mb-7 ${
                          plan.highlight ? 'bg-white text-orange-600 hover:bg-orange-50' : plan.ctaStyle
                        }`}
                      >
                        {plan.cta}
                      </Link>
                    )}

                    <ul className="flex flex-col gap-3">
                      {featureRows
                        .filter(
                          (f) => plan.features[f as keyof typeof plan.features] !== false
                        )
                        .map((f) => {
                          const val = plan.features[f as keyof typeof plan.features];
                          return (
                            <li key={f} className="flex items-start gap-2.5">
                              <i
                                className={`ri-check-line shrink-0 mt-0.5 ${
                                  plan.highlight ? 'text-white' : 'text-orange-500'
                                }`}
                              />
                              <span
                                className={`text-sm ${
                                  plan.highlight ? 'text-white/90' : 'text-stone-600'
                                }`}
                              >
                                {typeof val === 'string' ? (
                                  val
                                ) : (
                                  f
                                )}
                              </span>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                );
              })}
            </div>

            <p className="text-center text-sm text-stone-400 mt-8">
              Questions?{' '}
              <a href="mailto:hello@adforge.ai" className="text-orange-500 hover:underline">
                Contact us
              </a>
              .
            </p>
          </div>
        </section>

        <section className="py-16 bg-stone-50 px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-extrabold text-stone-900 text-center mb-10">
              Pricing FAQ
            </h2>
            <div className="flex flex-col gap-3">
              {pricingFaqs.map((faq, idx) => (
                <div
                  key={idx}
                  className={`rounded-2xl border transition-all duration-200 ${
                    faqOpen === idx ? 'border-orange-200 bg-orange-50/50' : 'border-stone-100 bg-white'
                  }`}
                >
                  <button
                    className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                    onClick={() => setFaqOpen(faqOpen === idx ? null : idx)}
                  >
                    <span
                      className={`text-base font-semibold ${
                        faqOpen === idx ? 'text-orange-600' : 'text-stone-800'
                      }`}
                    >
                      {faq.q}
                    </span>
                    <span
                      className={`w-6 h-6 flex items-center justify-center rounded-full shrink-0 ml-4 transition-all duration-200 ${
                        faqOpen === idx ? 'bg-orange-500 text-white rotate-180' : 'bg-stone-100 text-stone-500'
                      }`}
                    >
                      <i className="ri-arrow-down-s-line text-sm" />
                    </span>
                  </button>
                  {faqOpen === idx && (
                    <div className="px-6 pb-5">
                      <p className="text-stone-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-stone-950 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-400/30 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-sparkling-2-fill text-amber-300 text-xs" />
              <span className="text-sm font-semibold text-amber-200">Now includes Social Posts Generator</span>
            </div>
            <h2 className="text-4xl font-extrabold text-white mb-4">
              Ads <em>and</em> social content —{' '}
              <span className="text-orange-400">start free today</span>
            </h2>
            <p className="text-white/60 mb-8">No contracts. No credit card required. Upgrade anytime.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-400 text-white font-bold px-8 py-4 rounded-xl hover:from-orange-600 hover:to-amber-500 transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-flashlight-fill" />
                Get Started Free
              </Link>
              <Link
                to="/social-quick-start"
                className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white font-semibold px-8 py-4 rounded-xl hover:bg-white/20 transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-sparkling-2-line" />
                Try Social Posts
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
